import React from 'react';
import { useTranslation } from 'react-i18next';

import useReportQueryParams from '../../../../hooks/use-report-query-params';
import OverallCurriculumProgressChart from '../../charts/engagement/OverallCurriculumProgressChart';
import TotalActiveStudentsChart from '../../charts/engagement/TotalActiveStudentsChart';
import ActivitiesCompletedTimeSpentChart from '../../charts/engagement/ActivitiesCompletedTimeSpentChart';
import ReportCard from 'components/ReportsModule/components/common/ReportCard';
import { ReportEntityType } from 'components/ReportsModule/types';
import { KeyOrganizationClassroomEngagementReportResult } from 'types/schema-generated-types';

const EngagementChartView: React.FC<{
  reportData: KeyOrganizationClassroomEngagementReportResult[];
  loading: boolean;
}> = ({ reportData = [], loading }) => {
  const { t } = useTranslation(['Reports', 'ReportsHelpCharts', 'ReportsHelpCommon']);
  const [, setQuery] = useReportQueryParams();

  const [
    labels,
    {
      ids,
      curriculumProgressPercentage,
      totalStudentCount,
      activeStudentCount,
      totalActivitiesCompletedCount,
      averageWeeklyMinutesSpent
    }
  ] = reportData.reduce(
    (
      acc,
      {
        classroom,
        curriculumProgressPercentage,
        totalStudentCount,
        activeStudentCount,
        totalActivitiesCompletedCount,
        averageWeeklyMinutesSpent
      }
    ) => {
      if (!classroom) return acc;
      const [classroomNames, stats] = acc;

      classroomNames.push(classroom.name);
      stats.ids.push(classroom.id);
      stats.curriculumProgressPercentage.push(curriculumProgressPercentage);
      stats.totalStudentCount.push(totalStudentCount);
      stats.activeStudentCount.push(activeStudentCount);
      stats.totalActivitiesCompletedCount.push(totalActivitiesCompletedCount);
      stats.averageWeeklyMinutesSpent.push(averageWeeklyMinutesSpent);
      return acc;
    },
    [
      [],
      {
        ids: [],
        curriculumProgressPercentage: [],
        activeStudentCount: [],
        totalStudentCount: [],
        totalActivitiesCompletedCount: [],
        averageWeeklyMinutesSpent: []
      }
    ] as [string[], any]
  );

  const handleChartClick = (index: number): void => {
    setQuery({ entityType: ReportEntityType.CLASSROOM, entityId: ids[index] }, 'pushIn');
  };

  return (
    <>
      <ReportCard
        title={t('Overall Curriculum Progress')}
        helpText={t('curriculumProgressBySchoolClass', { ns: 'ReportsHelpCommon' })}
        loading={loading}
        reportData={reportData}
      >
        <OverallCurriculumProgressChart
          series={curriculumProgressPercentage}
          labels={labels}
          onClick={handleChartClick}
        />
      </ReportCard>
      <ReportCard
        title={t('Total/Active Students')}
        helpText={t('totalAndActiveStudentsBySchoolClass', { ns: 'ReportsHelpCharts' })}
        loading={loading}
        reportData={reportData}
      >
        <TotalActiveStudentsChart
          activeStudentsSeries={activeStudentCount}
          totalStudentsSeries={totalStudentCount}
          labels={labels}
          onClick={handleChartClick}
        />
      </ReportCard>
      <ReportCard
        title={t('Activities & Average Weekly Time Spent')}
        helpText={t('activitiesCompletedAndTimeSpentBySchoolClass', {
          ns: 'ReportsHelpCharts'
        })}
        loading={loading}
        reportData={reportData}
      >
        <ActivitiesCompletedTimeSpentChart
          activitiesCompletedSeries={totalActivitiesCompletedCount}
          timeSpentSeries={averageWeeklyMinutesSpent}
          labels={labels}
          onClick={handleChartClick}
        />
      </ReportCard>
    </>
  );
};

export default EngagementChartView;
